'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useLayoutEffect, useState } from 'react'
import { Device } from 'types/masterData'

import mediaQ from '../styles/MatchMediaQueries.module.scss'

const CHANGE_EVENT_NAME = 'change'
type MatchedMedia = boolean[]
export type MatchMediaQueries = string[]

const getDefaultValues = (queries: MatchMediaQueries, device: Device) => {
	const mediaQueries = {
		[Device.Mobile]: [mediaQ.issmall, mediaQ.ismedium],
		[Device.Desktop]: [mediaQ.islargeorgreater],
	}

	return queries.map((q) => mediaQueries[device].includes(q))
}

/**
 * Hook that returns an array of booleans that represent the result of the media queries passed as argument.
 * @example
 * const [isSmall, isMedium] = useMatchMedia(['(max-width: 600px)', '(min-width: 600px) and (max-width: 1024px)'])
 */
export function useMatchMedia(queries: MatchMediaQueries): MatchedMedia {
	const { device } = useMasterData()
	const [matches, setMatches] = useState<MatchedMedia>(
		getDefaultValues(queries, device)
	)

	useLayoutEffect(() => {
		const mediaQueries = queries.flatMap((q) => window.matchMedia(q) || [])
		const getMatches = (): MatchedMedia => {
			return mediaQueries.map((mq) => mq.matches)
		}

		setMatches(getMatches)

		function changeHandler() {
			setMatches(getMatches)
		}

		mediaQueries.forEach((mq) => {
			mq.addEventListener(CHANGE_EVENT_NAME, changeHandler)
		})

		return () => {
			mediaQueries.forEach((mq) => {
				mq.removeEventListener(CHANGE_EVENT_NAME, changeHandler)
			})
		}
	}, [])

	return matches
}
